import { useLocalization } from '@components/Localization'
import LocalizedLink from '@components/Localization/LocalizedLink'
import { Footer, Header, Page, PageHeader, PageTransition } from '@components/Page'
import FaqBlock from '@components/_faq/FaqBlock'
import { Question } from '@components/_faq/Question'
import ServiceSection from '@components/ServiceSection'
import { Box, Container, Grid, Heading, Link } from '@gassan-ui'
import { SingleItemResponse } from '@lib/kontent'
import { FaqPage } from '@lib/kontent/models/FaqPage'
import { NextPage } from 'next'
import React from 'react'
import { pickSeoProps } from '@components/Page/Page'
import nlUrlSlugItems from '@components/Localization/messages/urlSlugItems/nl'
import enUrlSlugItems from '@components/Localization/messages/urlSlugItems/en'

type Props = SingleItemResponse<FaqPage>

const urlCategoryMap = {
  watchServiceQuestions: 'watch-service',
  shippingDeliveryQuestions: 'shipping-and-delivery',
  returnRefundQuestions: 'return-and-refund',
  payQuestions: 'payments',
  storesQuestions: 'stores',
  jewelServiceQuestions: 'jewel-service'
}

const Faq: NextPage<Props> = ({ item, modular_content }) => {
  const { lang, messages } = useLocalization()
  const {
    watch_service_questions: watchServiceQuestions,
    shipping_delivery_questions: shippingDeliveryQuestions,
    return_refund_questions: returnRefundQuestions,
    pay_questions: payQuestions,
    stores_questions: storesQuestions,
    jewel_service_questions: jewelServiceQuestions
  } = item.elements
  const categories = {
    watchServiceQuestions,
    shippingDeliveryQuestions,
    returnRefundQuestions,
    payQuestions,
    storesQuestions,
    jewelServiceQuestions
  }

  const findMatchingCategory = (urlSlug: string) => {
    for (const key in categories) {
      const isMatch = categories[key].value
        .map(key => modular_content[key])
        .find(item => item.elements.url_slug.value === urlSlug)
      if (isMatch) return urlCategoryMap[key] && urlCategoryMap[key]
    }
  }

  const mostViewedLinksCategories = item.elements.most_viewed_questions.value.map(key => {
    const item = modular_content[key]
    return findMatchingCategory(item.elements.url_slug.value)
  })

  return (
    <Page
      {...pickSeoProps(item.elements)}
      languageAlternates={[
        {
          href: `/nl/${nlUrlSlugItems.faqPage}`,
          hrefLang: 'nl'
        },
        {
          href: `/en/${enUrlSlugItems.faqPage}`,
          hrefLang: 'en'
        }
      ]}
    >
      <Header initialVariant="absolute" />
      <PageTransition>
        <PageHeader
          title={item.elements.title.value}
          text={item.elements.description.value}
          link={
            <LocalizedLink href="/[...slug]" as="/contact">
              <Link variant="brown">{lang === 'nl' ? 'Neem contact op' : 'Contact us'}</Link>
            </LocalizedLink>
          }
        ></PageHeader>
        <Container
          bg="pampasLight"
          p={{ _: '2rem 1rem', small: '2rem', large: '2.5rem' }}
          width={{ _: '100%', small: 'inherit' }}
        >
          <Heading variant="h3" as="h2" mb="1.5rem">
            {lang === 'nl' ? 'Meest bekeken' : 'Most viewed'}
          </Heading>
          <Grid
            maxWidth="95rem"
            gridTemplateColumns={{
              _: '1fr',
              large: item.elements.most_viewed_questions.value.length > 3 ? 'auto auto' : '1fr'
            }}
            gridColumnGap="7.5rem"
          >
            {item.elements.most_viewed_questions.value.map((key, index) => {
              const question = modular_content[key]
              if (messages.faq[mostViewedLinksCategories[index]]?.urlSlug) {
                return (
                  <Box display="block" key={index} mb="1rem">
                    <LocalizedLink
                      href={{
                        pathname: `/${messages.urlSlugItems.faqPage}/[slug]`,
                        query: { id: question.elements.url_slug.value }
                      }}
                      as={`/${item.elements.url_slug.value}/${
                        messages.faq[mostViewedLinksCategories[index]].urlSlug
                      }`}
                    >
                      <Question>{question.elements.title.value}</Question>
                    </LocalizedLink>
                  </Box>
                )
              }
              return null
            })}
          </Grid>
        </Container>
        <Container mt={{ _: '2.5rem', small: '3.5rem', large: '7.5rem' }} mb={{ small: '2.5rem' }}>
          <Heading variant="h2" as="h2">
            {lang === 'nl' ? 'Zoek antwoorden per onderwerp' : 'Find answers by subject'}
          </Heading>
          <Grid
            gridTemplateColumns={{ _: '1fr', small: '1fr 1fr', large: '1fr 1fr 1fr' }}
            gridColumnGap="2rem"
          >
            <FaqBlock
              parentUrlSlug={item.elements.url_slug.value}
              variant="shipping-and-delivery"
              items={shippingDeliveryQuestions.value.map(key => modular_content[key].elements)}
            />
            <FaqBlock
              parentUrlSlug={item.elements.url_slug.value}
              variant="payments"
              items={payQuestions.value.map(key => modular_content[key].elements)}
            />
            <FaqBlock
              parentUrlSlug={item.elements.url_slug.value}
              variant="return-and-refund"
              items={returnRefundQuestions.value.map(key => modular_content[key].elements)}
            />
            <FaqBlock
              parentUrlSlug={item.elements.url_slug.value}
              variant="stores"
              items={storesQuestions.value.map(key => modular_content[key].elements)}
            />
            <FaqBlock
              parentUrlSlug={item.elements.url_slug.value}
              variant="watch-service"
              items={watchServiceQuestions.value.map(key => modular_content[key].elements)}
            />
            <FaqBlock
              parentUrlSlug={item.elements.url_slug.value}
              variant="jewel-service"
              items={jewelServiceQuestions.value.map(key => modular_content[key].elements)}
            />
          </Grid>
        </Container>
        <ServiceSection
          title={lang === 'nl' ? 'Komt u er niet uit?' : 'Hard time figuring it out?'}
        />
        <Footer />
      </PageTransition>
    </Page>
  )
}

export default Faq
